import React, { useState, useRef, useEffect } from 'react';
import * as S from './ResizablePanel.styles';

interface ResizablePanelProps {
  children: React.ReactNode;
  direction: 'horizontal' | 'vertical';
  onResize?: (size: number) => void;
  initialSize?: number;
  minSize?: number;
  maxSize?: number;
  $resizeFromLeft?: boolean;
}

export const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  direction,
  onResize,
  initialSize,
  minSize = 200,
  maxSize = 800,
  $resizeFromLeft = false,
}) => {
  const [size, setSize] = useState(initialSize || (direction === 'horizontal' ? 320 : 400));
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef<number>(0);
  const startSizeRef = useRef<number>(0);

  useEffect(() => {
    if (initialSize !== undefined) {
      setSize(initialSize);
    }
  }, [initialSize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
    startSizeRef.current = size;
    
    const handleMouseMove = (e: MouseEvent) => {
      const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
      let delta = currentPos - startPosRef.current;
      
      if ($resizeFromLeft && direction === 'horizontal') {
        delta = -delta;
      }
      
      const newSize = Math.max(minSize, Math.min(maxSize, startSizeRef.current + delta));
      
      setSize(newSize);
      onResize?.(newSize);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <S.ResizablePanelContainer
      ref={panelRef}
      $direction={direction}
      $size={size}
      $isResizing={isResizing}
    >
      {children}
      <S.ResizeHandle
        $direction={direction}
        onMouseDown={handleMouseDown}
        $isResizing={isResizing}
        $resizeFromLeft={$resizeFromLeft}
      />
    </S.ResizablePanelContainer>
  );
};

